/**
 * Ranks required-action events into a case's headline action + "+N more".
 *
 * Order principle: lead with the one hard gate, then favour low-friction
 * momentum over money asks — so payment authorizations never headline above an
 * enabler. Ties break on recency.
 */

import { issue__EventType } from "@medbillai/generated-graphql-types";

/** Minimal shape an action needs to be ranked — decoupled from any one query. */
export type RankableAction = {
  eventType: issue__EventType;
  actionRequired: boolean;
  /** ISO timestamp of the event, used as the recency tie-break. */
  sentAt?: string | null;
};

/**
 * Event types in headline priority order. Earlier = higher priority.
 * Event types absent from this list are never preferred as the headline over a
 * listed one; among themselves they fall back to the recency tie-break.
 */
const HEADLINE_PRIORITY: readonly issue__EventType[] = [
  // Hard gate: sign HIPAA / release.
  issue__EventType.HIPAA_RELEASE_REQUESTED,
  issue__EventType.TEMPLATE_RELEASE_REQUESTED,
  // Enablers — low-friction momentum.
  issue__EventType.CONNECT_INSURANCE_REQUESTED,
  issue__EventType.CONNECT_ACCOUNT_REQUESTED,
  issue__EventType.DOCUMENT_UPLOADED,
  issue__EventType.FA_FLOW,
  // Payment authorizations — money asks, never above an enabler.
  issue__EventType.PAYMENT_AGREEMENT_REQUESTED,
  issue__EventType.BILL_PAY_REQUESTED,
  issue__EventType.BILL_PAYMENT_REQUESTED,
  issue__EventType.CLOSURE_PAYMENT_REQUESTED,
  issue__EventType.PAYMENT_CHARGE_FAILED,
  // Chat reply.
  issue__EventType.CHAT,
];

const PRIORITY_RANK: ReadonlyMap<issue__EventType, number> = new Map(
  HEADLINE_PRIORITY.map((eventType, index) => [eventType, index]),
);

/** Unlisted event types sort after every listed one, then by recency. */
const UNRANKED = Number.MAX_SAFE_INTEGER;

const rankOf = (eventType: issue__EventType): number =>
  PRIORITY_RANK.get(eventType) ?? UNRANKED;

/**
 * Compare two actions by headline priority, then by recency (newer first).
 * Returns negative if `a` should headline over `b`.
 */
function compareHeadline(a: RankableAction, b: RankableAction): number {
  const rankDelta = rankOf(a.eventType) - rankOf(b.eventType);
  if (rankDelta !== 0) {
    return rankDelta;
  }
  // Tie-break: most-recent actionRequired event first. Missing timestamps sort
  // last so a dated action always wins the tie.
  const aTime = a.sentAt ? Date.parse(a.sentAt) : Number.NEGATIVE_INFINITY;
  const bTime = b.sentAt ? Date.parse(b.sentAt) : Number.NEGATIVE_INFINITY;
  // Guard the two-undated case: -Infinity - -Infinity is NaN, not a tie.
  if (aTime === bTime) {
    return 0;
  }
  return bTime - aTime;
}

export type HeadlineActions<T extends RankableAction> = {
  /** Highest-priority action-required event, or null if the case has none. */
  headline: T | null;
  /** Count of remaining action-required events folded into "+N more" (≥ 0). */
  moreCount: number;
};

/**
 * Pick the headline action-required event and count the rest. Non-action-required
 * events (e.g. merely-unread staff messages) are ignored entirely.
 */
export function rankHeadlineAction<T extends RankableAction>(
  actions: readonly T[] | null | undefined,
): HeadlineActions<T> {
  const actionRequired = (actions ?? []).filter(
    action => action.actionRequired,
  );
  const [first, ...rest] = actionRequired;
  if (first === undefined) {
    return { headline: null, moreCount: 0 };
  }
  const headline = rest.reduce(
    (best, candidate) =>
      compareHeadline(candidate, best) < 0 ? candidate : best,
    first,
  );
  return { headline, moreCount: actionRequired.length - 1 };
}
